import React, { useEffect, useState } from "react";
import { DeviceMobile, X, Export, Plus } from "@phosphor-icons/react";

const DISMISS_KEY = "pitchiq_install_dismissed_at";
const DISMISS_DAYS = 14; // resurface after 2 weeks

const isStandalone = () => {
  // Detects if the PWA is already installed/launched from home screen
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag
    window.navigator.standalone === true
  );
};

const isMobile = () => {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const isDismissedRecently = () => {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isStandalone() || !isMobile() || isDismissedRecently()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari never fires beforeinstallprompt — show after a short delay
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setShow(true), 3000);
    }

    const onInstalled = () => {
      setShow(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, Date.now().toString()); } catch { /* noop */ }
    setShow(false);
    setShowIosHelp(false);
  };

  const handleInstall = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* noop */ }
      setDeferred(null);
      setShow(false);
      try { localStorage.setItem(DISMISS_KEY, Date.now().toString()); } catch { /* noop */ }
    } else if (isIOS()) {
      setShowIosHelp(true);
    }
  };

  if (!show) return null;

  return (
    <>
      {/* Banner */}
      <div
        data-testid="install-prompt"
        className="fixed left-4 right-4 bottom-4 z-[80] md:left-auto md:right-6 md:bottom-6 md:max-w-sm"
      >
        <div className="surface border border-primary-yellow/40 bg-[#0F1116]/95 backdrop-blur-xl shadow-[8px_8px_0_rgba(227,255,0,0.15)] p-4 flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 grid place-items-center bg-primary-yellow text-black">
            <DeviceMobile size={22} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-primary-yellow font-bold">Install PitchIQ</div>
            <p className="text-sm text-white mt-1 leading-snug">
              Add to your home screen for one-tap predictions and a full-screen experience.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="install-prompt-action"
                onClick={handleInstall}
                className="bg-primary-yellow text-black px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold hover:-translate-y-0.5 transition-transform"
              >
                {isIOS() ? "Show me how" : "Install"}
              </button>
              <button
                data-testid="install-prompt-dismiss"
                onClick={dismiss}
                className="text-white/50 hover:text-white px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            aria-label="Close install prompt"
            data-testid="install-prompt-close"
            onClick={dismiss}
            className="shrink-0 text-white/40 hover:text-white"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>

      {/* iOS modal helper */}
      {showIosHelp && (
        <div
          data-testid="install-ios-help"
          className="fixed inset-0 z-[90] grid place-items-center bg-black/70 backdrop-blur-sm px-4"
          onClick={() => setShowIosHelp(false)}
        >
          <div
            className="w-full max-w-sm surface border border-white/15 bg-[#0F1116] p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              aria-label="Close"
              onClick={() => setShowIosHelp(false)}
              className="absolute top-3 right-3 text-white/50 hover:text-white"
            >
              <X size={18} weight="bold" />
            </button>
            <div className="text-[10px] uppercase tracking-[0.25em] text-primary-yellow font-bold">Install on iPhone</div>
            <h3 className="font-heading text-2xl uppercase font-black mt-2">Add PitchIQ to home screen</h3>
            <ol className="mt-5 space-y-4 text-sm text-white/80">
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 grid place-items-center bg-primary-yellow text-black font-bold text-xs">1</span>
                <span className="flex-1">
                  Tap the <Export size={16} weight="bold" className="inline align-text-bottom mx-1" /> <strong>Share</strong> button in Safari&apos;s toolbar.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 grid place-items-center bg-primary-yellow text-black font-bold text-xs">2</span>
                <span className="flex-1">
                  Scroll and choose <Plus size={16} weight="bold" className="inline align-text-bottom mx-1" /> <strong>Add to Home Screen</strong>.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 grid place-items-center bg-primary-yellow text-black font-bold text-xs">3</span>
                <span className="flex-1">
                  Tap <strong>Add</strong> — the yellow soccer ball icon appears on your home screen.
                </span>
              </li>
            </ol>
            <button
              onClick={dismiss}
              className="mt-6 w-full bg-primary-yellow text-black font-bold uppercase tracking-[0.2em] py-3 text-xs hover:-translate-y-0.5 transition-transform"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};
